/**
 * SEO helpers — runtime injection of <title>, <meta>, <link rel=canonical>,
 * Open Graph + Twitter tags, and JSON-LD blocks into <head>.
 *
 * Read-only: pulls Meta Title / Meta Description / SEO Body from the data
 * already loaded by the page. Never writes back to the API.
 *
 * Usage:
 *   useSeoHead({
 *     title, description, canonical, image,
 *     ogType, jsonLd: [productSchema, breadcrumbSchema, faqSchema],
 *   });
 */
import { useEffect } from "react";

const ORIGIN = "https://www.koltprinting.com";

/* ───────────────────── DOM helpers ───────────────────── */

function setMeta(attr, name, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Inject (or replace) a JSON-LD script tag keyed by id so updates are idempotent. */
function setJsonLd(id, data) {
  // Prerendered pages ship static JSON-LD; drop it once the live app owns the head
  // so crawlers executing JS never see two Product / BreadcrumbList entities.
  document.head.querySelectorAll('script[type="application/ld+json"][data-prerender]').forEach((n) => n.remove());
  const sel = `script[type="application/ld+json"][data-seo-id="${id}"]`;
  let el = document.head.querySelector(sel);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.dataset.seoId = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/* ───────────────────── Public hook ───────────────────── */

export function useSeoHead({
  title,
  description,
  canonical,            // path or full URL — we'll prepend ORIGIN if it's a path
  image,                // absolute or path; OG/Twitter use this
  ogType = "website",
  twitterCard = "summary_large_image",
  jsonLd = [],          // array of objects { id, data }
} = {}) {
  // Stable fingerprint so the effect re-runs only when the schema actually changes.
  const jsonLdKey = JSON.stringify(jsonLd.map((b) => b && [b.id, b.data && JSON.stringify(b.data).length]));
  useEffect(() => {
    if (title) document.title = title;

    setMeta("name", "description", description);

    const canonicalHref = (() => {
      if (!canonical) return null;
      if (/^https?:\/\//i.test(canonical)) return canonical;
      return ORIGIN + (canonical.startsWith("/") ? canonical : `/${canonical}`);
    })();
    setLink("canonical", canonicalHref);

    const imageHref = (() => {
      if (!image) return null;
      if (/^https?:\/\//i.test(image)) return image;
      return ORIGIN + (image.startsWith("/") ? image : `/${image}`);
    })();

    setMeta("property", "og:type", ogType);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonicalHref);
    setMeta("property", "og:image", imageHref);
    setMeta("property", "og:site_name", "Kolt Printing");

    setMeta("name", "twitter:card", twitterCard);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", imageHref);

    // JSON-LD blocks (idempotent by id).
    for (const block of jsonLd) {
      if (block && block.id) setJsonLd(block.id, block.data);
    }
    // Clean up *only* the ids we own when the component unmounts — leaves
    // unrelated head tags (like the site-wide Organization schema in
    // index.html) alone.
    return () => {
      for (const block of jsonLd) {
        if (block && block.id) setJsonLd(block.id, null);
      }
    };
    // jsonLd is intentionally tracked via jsonLdKey: callers often pass a fresh
    // array literal each render, which would re-run the effect on every paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    description,
    canonical,
    image,
    ogType,
    twitterCard,
    jsonLdKey,
  ]);
}

/* ───────────────────── Schema builders ───────────────────── */

export function breadcrumbSchema(crumbs) {
  // crumbs = [{ name, url }] in display order. URL is a path.
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.url ? (/^https?:\/\//i.test(c.url) ? c.url : ORIGIN + c.url) : undefined,
    })),
  };
}

export function productSchema({ product, url, image, price }) {
  const sku =
    product.sku_prefix && product.product_id
      ? `${product.sku_prefix}-${product.product_id.slice(-5).toUpperCase()}`
      : product.product_id || product.slug;
  const description =
    product.meta_description ||
    product.short_description ||
    product.description ||
    "";
  const offerPrice = (() => {
    const n =
      Number(price && (price.unit_price || price.total || price.starting_at)) ||
      Number(product.base_price) ||
      undefined;
    return n && Number.isFinite(n) && n > 0 ? n.toFixed(2) : undefined;
  })();
  const img = image && /^https?:\/\//i.test(image) ? image : (image ? ORIGIN + image : undefined);
  const full = url && /^https?:\/\//i.test(url) ? url : (url ? ORIGIN + url : undefined);
  // AggregateRating — only emitted when the product has REAL customer
  // ratings stored on the record (`rating_count > 0`). We never publish a
  // synthetic 4.7/5 because Google penalises fake structured data and, more
  // importantly, it's dishonest to customers. Populate the fields on the
  // product doc as `rating_value` + `rating_count` when reviews land.
  const rc = Number(product.rating_count || 0);
  const rv = Number(product.rating_value || 0);
  const aggregateRating = (rc > 0 && rv > 0)
    ? {
        "@type":       "AggregateRating",
        ratingValue:   rv.toFixed(1),
        reviewCount:   rc,
        bestRating:    "5",
        worstRating:   "1",
      }
    : undefined;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description,
    sku,
    url: full,
    image: img,
    brand: { "@type": "Brand", name: "Kolt Printing" },
    aggregateRating,
    offers: offerPrice
      ? {
          "@type": "Offer",
          url: full,
          price: offerPrice,
          priceCurrency: "USD",
          availability: "https://schema.org/InStock",
          seller: { "@type": "Organization", name: "Kolt Printing" },
          shippingDetails: OFFER_SHIPPING_DETAILS,
          hasMerchantReturnPolicy: MERCHANT_RETURN_POLICY,
        }
      : undefined,
  };
}

// Merchant-listing fields Google now expects on every Offer: honest ground
// shipping (matches the Merchant Center feed) and the custom-print return policy.
export const OFFER_SHIPPING_DETAILS = {
  "@type": "OfferShippingDetails",
  shippingRate: { "@type": "MonetaryAmount", value: "14.99", currency: "USD" },
  shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" },
  deliveryTime: {
    "@type": "ShippingDeliveryTime",
    handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 5, unitCode: "DAY" },
    transitTime: { "@type": "QuantitativeValue", minValue: 5, maxValue: 7, unitCode: "DAY" },
  },
};
export const MERCHANT_RETURN_POLICY = {
  "@type": "MerchantReturnPolicy",
  applicableCountry: "US",
  returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
  merchantReturnLink: ORIGIN + "/refund-policy",
  description: "Custom-printed to your artwork — free reprint or full refund for defects, damage or our error reported within 7 days of delivery.",
};

export function collectionPageSchema({ category, products, url }) {
  const full = url && /^https?:\/\//i.test(url) ? url : (url ? ORIGIN + url : undefined);
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: category.meta_title || category.name,
    description:
      category.meta_description ||
      category.longDesc ||
      category.short ||
      category.description ||
      "",
    url: full,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: products.length,
      itemListElement: products.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: ORIGIN + `/shop/product/${p.slug || p.product_id}`,
        name: p.name,
      })),
    },
  };
}

/**
 * Parse Q&A pairs out of an SEO body. Supports several common patterns:
 *   - "Q: What is X?\nA: ..."
 *   - Markdown headings ending in "?" followed by their next paragraph
 *   - Bold/plain question lines ending in "?" then a body line
 * Returns null if we couldn't find at least one valid pair.
 */
export function faqSchemaFromBody(body) {
  const split = splitSeoBody(body);
  if (!split || split.faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: split.faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

/**
 * Split a raw SEO body into:
 *   - `prefix`: everything before the first Q:/A: pair (rendered as-is)
 *   - `faqs`:   array of {q, a} parsed from the Q:/A: block
 *   - `suffix`: anything trailing after the last detected A: block
 *
 * Designed to fail gracefully: returns `{prefix: body, faqs: [], suffix: ""}`
 * when no valid Q/A pairs are found, so callers can render the body normally.
 */
export function splitSeoBody(body) {
  const text = (body || "").toString();
  if (!text.trim()) return { prefix: "", faqs: [], suffix: "" };

  // Pattern A: explicit Q:/A: prefixes (case-insensitive). Captures the first
  // index where Q: appears so we can preserve any pre-amble verbatim.
  const lines = text.split(/\r?\n/);
  const firstQIdx = lines.findIndex((ln) => /^\s*Q\s*[:.\-]/i.test(ln));

  if (firstQIdx === -1) {
    // No Q:/A: prefix style. Try fallback: heading-ending-in-? followed by
    // its next paragraph. Used only when at least 1 pair survives.
    const blocks = text.split(/\n{2,}/);
    const faqs = [];
    const consumedBlockIdx = new Set();
    for (let i = 0; i < blocks.length - 1; i++) {
      const candidate = blocks[i].replace(/^#+\s*/, "").replace(/^\*\*(.+)\*\*$/, "$1").trim();
      if (candidate.length > 6 && candidate.length < 240 && candidate.endsWith("?")) {
        const answer = blocks[i + 1].trim();
        if (answer && answer.length > 4) {
          faqs.push({ q: candidate, a: answer });
          consumedBlockIdx.add(i);
          consumedBlockIdx.add(i + 1);
        }
      }
    }
    if (faqs.length === 0) return { prefix: text, faqs: [], suffix: "" };
    const prefix = blocks.filter((_, i) => !consumedBlockIdx.has(i)).join("\n\n");
    return { prefix, faqs, suffix: "" };
  }

  // Pattern A path: walk lines from firstQIdx, pairing each Q: with its A:.
  const prefix = lines.slice(0, firstQIdx).join("\n").trim();
  const faqs = [];
  let curQ = null;
  let curA = [];
  let lastConsumedLine = firstQIdx - 1;
  for (let i = firstQIdx; i < lines.length; i++) {
    const ln = lines[i];
    const qMatch = ln.match(/^\s*Q\s*[:.\-]?\s*(.+?)\s*$/i);
    const aMatch = ln.match(/^\s*A\s*[:.\-]?\s*(.*)\s*$/i);
    if (qMatch && /^\s*Q\s*[:.\-]/i.test(ln)) {
      if (curQ && curA.length) {
        faqs.push({ q: curQ.trim(), a: curA.join("\n").trim() });
      }
      curQ = qMatch[1];
      curA = [];
      lastConsumedLine = i;
    } else if (aMatch && /^\s*A\s*[:.\-]/i.test(ln) && curQ) {
      curA = [aMatch[1]];
      lastConsumedLine = i;
    } else if (curQ && curA.length >= 0) {
      // Continuation line for the in-progress answer (blank lines OK).
      curA.push(ln);
      lastConsumedLine = i;
    }
  }
  if (curQ && curA.length) {
    faqs.push({ q: curQ.trim(), a: curA.join("\n").trim() });
  }
  // Drop any FAQ where the answer is empty (malformed).
  const cleanFaqs = faqs.filter((f) => f.q && f.a);
  if (cleanFaqs.length === 0) return { prefix: text, faqs: [], suffix: "" };
  const suffix = lines.slice(lastConsumedLine + 1).join("\n").trim();
  return { prefix, faqs: cleanFaqs, suffix };
}

/* ───────────────────── Misc ───────────────────── */

/** Strip "| ..." suffixes from a string so subcategory display names like
 *  "Custom Business Cards | Professional Printing from $0.25/card | Kolt"
 *  render as "Custom Business Cards" (or its first natural clause).         */
export function cleanDisplayName(name) {
  if (!name) return "";
  const first = String(name).split("|")[0].trim();
  return first || String(name).trim();
}

export const SEO_ORIGIN = ORIGIN;
